<template>
    <zen-message-modal
        :isOpen="isOpen"
        title="Review & Publish"
        :extraWide="true"
        iconVariant="sky"
    >
        <template v-slot:icon>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="h-6 w-6 text-sky-600"
            >
                <path d="M12 19V5" />
                <path d="m5 12 7-7 7 7" />
            </svg>
        </template>

        <div class="space-y-4">
            <p class="text-sm text-taupe-500">
                Review the changes for
                <span class="font-medium text-taupe-700">{{ flagName }}</span>
                below before publishing. The change will take effect
                {{
                    draftScheduleEnabled
                        ? 'at the scheduled time'
                        : 'immediately'
                }}
                for all API requests.
            </p>

            <!-- Error banner -->
            <div
                v-if="error"
                class="rounded-lg bg-red-50 ring-1 ring-red-200 p-3"
            >
                <div class="flex items-start gap-2">
                    <svg
                        class="h-5 w-5 shrink-0 text-red-500 mt-0.5"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M8.485 3.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 3.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
                            clip-rule="evenodd"
                        />
                    </svg>
                    <div>
                        <p class="text-sm font-medium text-red-800">
                            {{ error }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Value change summary -->
            <div class="rounded-lg border border-taupe-200 bg-taupe-50 p-4">
                <div class="flex items-center gap-2 mb-3">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        class="h-4 w-4 text-taupe-500"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
                            clip-rule="evenodd"
                        />
                    </svg>
                    <h4 class="text-sm font-medium text-taupe-800">
                        Target Value
                    </h4>
                </div>
                <!-- Stacked below md, where side by side leaves each box too narrow for a long value; the arrow turns to point down. -->
                <div
                    data-testid="review-value-change"
                    class="flex flex-col items-stretch gap-3 md:flex-row md:items-center"
                >
                    <div
                        class="min-w-0 rounded-md bg-white border border-taupe-200 px-3 py-2 md:flex-1"
                    >
                        <div class="text-xs text-taupe-500 mb-0.5">Current</div>
                        <div
                            data-testid="review-current-value"
                            class="font-mono text-sm text-taupe-700 wrap-anywhere"
                        >
                            {{
                                currentValueDisplay != ''
                                    ? currentValueDisplay
                                    : '(empty)'
                            }}
                        </div>
                    </div>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        data-testid="review-value-change-arrow"
                        class="h-5 w-5 shrink-0 self-center rotate-90 text-taupe-400 md:rotate-0"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z"
                            clip-rule="evenodd"
                        />
                    </svg>
                    <div
                        class="min-w-0 rounded-md bg-white border border-sky-200 px-3 py-2 ring-1 ring-sky-100 md:flex-1"
                    >
                        <div class="text-xs text-sky-600 mb-0.5">New</div>
                        <div
                            data-testid="review-new-value"
                            class="font-mono text-sm font-medium text-taupe-900 wrap-anywhere"
                        >
                            {{
                                selectedValueDisplay != ''
                                    ? selectedValueDisplay
                                    : '(empty)'
                            }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Target rules section -->
            <div
                v-if="contextRules && contextRules.length > 0"
                class="rounded-lg border border-taupe-200 bg-taupe-50 p-4"
            >
                <div class="flex items-center gap-2 mb-3">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        class="h-4 w-4 text-taupe-500"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M3.28 2.22a.75.75 0 00-1.06 1.06l14.5 14.5a.75.75 0 101.06-1.06l-1.745-1.745a10.029 10.029 0 003.3-4.38 1.651 1.651 0 000-1.185A10.004 10.004 0 009.999 3a9.956 9.956 0 00-4.744 1.194L3.28 2.22zM7.752 6.69l1.092 1.092a2.5 2.5 0 013.374 3.373l1.092 1.092a4 4 0 00-5.558-5.558z"
                            clip-rule="evenodd"
                        />
                        <path
                            d="M10.748 13.93l2.523 2.523a9.987 9.987 0 01-3.27.547c-4.258 0-7.894-2.66-9.337-6.41a1.651 1.651 0 010-1.186A10.007 10.007 0 012.839 6.02L6.07 9.252a4 4 0 004.678 4.678z"
                        />
                    </svg>
                    <h4 class="text-sm font-medium text-taupe-800">
                        Target Rules
                        <span
                            class="ml-1 inline-flex items-center justify-center rounded-full bg-taupe-200 px-2 py-0.5 text-xs font-medium text-taupe-700"
                        >
                            {{ contextRules.length }}
                        </span>
                    </h4>
                </div>
                <div class="space-y-2">
                    <div
                        v-for="(rule, index) in contextRules"
                        :key="rule.ulid"
                        class="flex items-start gap-2 rounded-md bg-white border border-taupe-200 px-3 py-2 text-sm"
                    >
                        <span
                            class="shrink-0 inline-flex items-center justify-center rounded-full bg-taupe-100 h-5 w-5 text-xs font-medium text-taupe-600"
                        >
                            {{ index + 1 }}
                        </span>
                        <div class="flex-1 min-w-0">
                            <div
                                class="flex flex-wrap items-center gap-1 text-taupe-700"
                            >
                                <span class="font-medium">{{
                                    rule.description || 'Rule'
                                }}</span>
                                <span
                                    v-if="
                                        rule.criteria &&
                                        rule.criteria.data &&
                                        rule.criteria.data[0]
                                    "
                                    class="text-taupe-500"
                                >
                                    &mdash;
                                    <span class="font-mono text-xs">{{
                                        rule.criteria.data[0].selector
                                    }}</span>
                                    <span
                                        v-if="
                                            rule.criteria.data[0].sub_selector
                                        "
                                        class="font-mono text-xs"
                                        >.{{
                                            rule.criteria.data[0].sub_selector
                                        }}</span
                                    >
                                    <span class="italic text-xs ml-1">{{
                                        formatComparer(
                                            rule.criteria.data[0].comparer
                                        )
                                    }}</span>
                                </span>
                                <!-- Matching values (inline) -->
                                <template
                                    v-if="
                                        rule.criteria &&
                                        rule.criteria.data &&
                                        rule.criteria.data[0]
                                    "
                                >
                                    <!-- Context values -->
                                    <template
                                        v-if="
                                            rule.criteria.data[0].selector ===
                                            'context'
                                        "
                                    >
                                        <span
                                            v-for="ctx in getPublishRuleContextValues(
                                                rule.criteria.data[0]
                                            )"
                                            :key="ctx.ulid || ctx.identifier"
                                            class="inline-flex items-center gap-1 rounded-full bg-taupe-100 px-2 py-0.5 text-xs text-taupe-700"
                                        >
                                            <span
                                                v-if="ctx.type"
                                                class="font-mono text-taupe-500 pr-1 border-r border-taupe-300"
                                                >{{ ctx.type }}</span
                                            >
                                            <span>{{ ctx.name }}</span>
                                        </span>
                                    </template>
                                    <!-- Segment values -->
                                    <template
                                        v-if="
                                            rule.criteria.data[0].selector ===
                                                'segment' &&
                                            rule.criteria.data[0].segments
                                        "
                                    >
                                        <span
                                            v-for="segment in rule.criteria
                                                .data[0].segments.data"
                                            :key="segment.ulid"
                                            class="inline-flex items-center rounded-full bg-taupe-100 px-2 py-0.5 text-xs text-taupe-700"
                                        >
                                            {{ segment.name }}
                                        </span>
                                    </template>
                                    <!-- Attribute values -->
                                    <template
                                        v-if="
                                            rule.criteria.data[0].selector ===
                                                'attribute' &&
                                            rule.criteria.data[0].comparer !==
                                                'isnull' &&
                                            rule.criteria.data[0].comparer !==
                                                'notnull' &&
                                            rule.criteria.data[0].values
                                        "
                                    >
                                        <span
                                            v-for="val in rule.criteria.data[0]
                                                .values.data"
                                            :key="val.ulid"
                                            class="inline-flex items-center rounded-full bg-taupe-100 px-2 py-0.5 text-xs text-taupe-700"
                                        >
                                            {{ val.value }}
                                        </span>
                                    </template>
                                </template>
                            </div>
                            <div class="text-xs text-taupe-500 mt-1">
                                Serve:
                                <span
                                    data-testid="review-rule-served-value"
                                    class="font-mono font-medium text-taupe-700 wrap-anywhere"
                                    >{{
                                        rule.value?.data?.display || '(empty)'
                                    }}</span
                                >
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Percentage rollout section -->
            <div
                v-if="draftRolloutData && draftRolloutData.mode"
                class="rounded-lg border border-taupe-200 bg-taupe-50 p-4"
            >
                <div class="flex items-center gap-2 mb-3">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        class="h-4 w-4 text-taupe-500"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-11.25a.75.75 0 00-1.5 0v2.5h-2.5a.75.75 0 000 1.5h2.5v2.5a.75.75 0 001.5 0v-2.5h2.5a.75.75 0 000-1.5h-2.5v-2.5z"
                            clip-rule="evenodd"
                        />
                    </svg>
                    <h4 class="text-sm font-medium text-taupe-800">
                        Percentage Rollout
                    </h4>
                </div>
                <div
                    class="flex items-center gap-4 rounded-md bg-white border border-taupe-200 px-3 py-2"
                >
                    <div class="flex-1">
                        <div class="text-xs text-taupe-500 mb-0.5">Mode</div>
                        <div
                            class="text-sm font-medium text-taupe-800 capitalize"
                        >
                            {{ draftRolloutData.mode }}
                        </div>
                    </div>
                    <div
                        v-if="
                            draftRolloutData.mode === 'manual' &&
                            draftRolloutData.percentage != null
                        "
                        class="flex-1"
                    >
                        <div class="text-xs text-taupe-500 mb-0.5">
                            Starting Percentage
                        </div>
                        <div class="text-sm font-medium text-taupe-800">
                            {{ draftRolloutData.percentage }}%
                        </div>
                    </div>
                    <div
                        v-if="draftRolloutData.mode === 'automatic'"
                        class="flex-1"
                    >
                        <div class="text-xs text-taupe-500 mb-0.5">
                            Starting Percentage
                        </div>
                        <div class="text-sm font-medium text-taupe-800">
                            {{ draftRolloutData.percentage || 1 }}%
                        </div>
                    </div>
                </div>
                <p class="mt-2 text-xs text-taupe-500">
                    <template v-if="draftRolloutData.mode === 'automatic'">
                        Traffic will be automatically ramped up in stages until
                        it reaches 100%.
                    </template>
                    <template v-else>
                        Traffic will be set to the configured percentage. You
                        can adjust it manually after publishing.
                    </template>
                </p>
            </div>

            <!-- Schedule section -->
            <div
                v-if="draftScheduleEnabled"
                class="rounded-lg border border-taupe-200 bg-taupe-50 p-4"
            >
                <div class="flex items-center gap-2 mb-3">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        class="h-4 w-4 text-taupe-500"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 000-1.5h-3.25V5z"
                            clip-rule="evenodd"
                        />
                    </svg>
                    <h4 class="text-sm font-medium text-taupe-800">
                        Scheduled Publish
                    </h4>
                </div>
                <div
                    class="rounded-md bg-white border border-taupe-200 px-3 py-2"
                >
                    <div class="text-xs text-taupe-500 mb-0.5">Publish at</div>
                    <div class="text-sm font-medium text-taupe-900">
                        <zen-date-time :value="scheduledAt" format="short" />
                    </div>
                </div>
                <p class="mt-2 text-xs text-taupe-500">
                    This change will not take effect until the scheduled time.
                </p>
            </div>

            <!-- Comment & Confirmation -->
            <div
                class="rounded-lg border border-taupe-200 bg-taupe-50 p-4 space-y-4"
            >
                <div>
                    <label
                        for="publish-comment"
                        class="block text-sm font-medium text-taupe-800"
                    >
                        Comment
                        <span
                            v-if="commentsRequired"
                            class="text-red-500 font-normal italic"
                        >
                            (Required)
                        </span>
                    </label>
                    <div class="mt-1.5">
                        <textarea
                            :value="comment"
                            @input="
                                $emit('update:comment', $event.target.value)
                            "
                            id="publish-comment"
                            name="comment"
                            rows="2"
                            placeholder="Describe the purpose of this change..."
                            class="block w-full rounded-md border-taupe-300 bg-white shadow-xs focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                        ></textarea>
                    </div>
                </div>

                <div v-if="confirmationRequired">
                    <label
                        for="publish-confirmation"
                        class="block text-sm font-medium text-taupe-800"
                    >
                        Confirmation
                        <span class="text-red-500 font-normal italic"
                            >(Required)</span
                        >
                    </label>
                    <p class="mt-0.5 text-xs text-taupe-500">
                        Type
                        <span class="font-mono font-medium">{{ flagKey }}</span>
                        to confirm.
                    </p>
                    <div class="mt-1.5">
                        <input
                            type="text"
                            :value="confirmation"
                            @input="
                                $emit(
                                    'update:confirmation',
                                    $event.target.value
                                )
                            "
                            id="publish-confirmation"
                            name="confirmation"
                            :placeholder="flagKey"
                            class="block w-full rounded-md border-taupe-300 bg-white shadow-xs focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                        />
                    </div>
                </div>
            </div>
        </div>

        <template v-slot:buttons>
            <zen-button
                variant="secondary"
                @click="$emit('close')"
                :disabled="processing"
            >
                Cancel
            </zen-button>
            <zen-button
                variant="primary"
                @click="$emit('confirm')"
                :disabled="!allowTargetUpdate || processing"
            >
                {{
                    processing
                        ? 'Please wait'
                        : draftScheduleEnabled
                          ? 'Schedule'
                          : 'Publish'
                }}
            </zen-button>
        </template>
    </zen-message-modal>
</template>

<script>
import {
    formatComparer,
    getContextDisplayValues,
} from '@/utils/target-rule-display'

export default {
    name: 'ReviewPublishModal',
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        error: {
            type: String,
            default: null,
        },
        flagName: {
            type: String,
            default: null,
        },
        flagKey: {
            type: String,
            default: null,
        },
        currentValueDisplay: {
            type: String,
            default: null,
        },
        selectedValueDisplay: {
            type: String,
            default: null,
        },
        contextRules: {
            type: Array,
            default: () => [],
        },
        draftRolloutData: {
            type: Object,
            default: null,
        },
        draftScheduleEnabled: {
            type: Boolean,
            default: false,
        },
        scheduledAt: {
            type: [String, Date],
            default: null,
        },
        commentsRequired: {
            type: Boolean,
            default: false,
        },
        confirmationRequired: {
            type: Boolean,
            default: false,
        },
        allowTargetUpdate: {
            type: Boolean,
            default: true,
        },
        processing: {
            type: Boolean,
            default: false,
        },
        comment: {
            type: String,
            default: null,
        },
        confirmation: {
            type: String,
            default: null,
        },
    },
    emits: ['close', 'confirm', 'update:comment', 'update:confirmation'],
    methods: {
        formatComparer,
        getPublishRuleContextValues: getContextDisplayValues,
    },
}
</script>
